<script setup lang="ts">
import { ref, useId, watch } from 'vue';

import Input from '@/components/base/form/Input.vue';
import RadioGroupItem from '@/components/base/form/RadioGroupItem.vue';
import TextArea from '@/components/base/form/TextArea.vue';
import TextButton from '@/components/base/TextButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';

const props = defineProps<{ containsGift?: boolean }>();

const includeGiftMessage = defineModel<boolean>('includeGiftMessage', {
  required: true,
});
const message = defineModel<string>('message', {
  required: true,
});
const senderName = defineModel<string>('sender');

const isOpen = ref(props.containsGift || includeGiftMessage.value);
const uniqueId = useId();

type Choice = 'includeGiftMessage' | 'noGift';
const giftOption = ref<Choice>(includeGiftMessage.value ? 'includeGiftMessage' : 'noGift');
watch(giftOption, () => {
  if (giftOption.value === 'noGift') {
    includeGiftMessage.value = false;
    message.value = '';
    senderName.value = '';
  } else if (giftOption.value === 'includeGiftMessage') {
    includeGiftMessage.value = true;
  }
});

const maxLength = 300;
const cleanseEmojis = (event: { target: { value: string } }) => {
  // eslint-disable-next-line no-param-reassign
  event.target.value = event.target.value.replace(/[^\p{L}\p{N}\p{P}\p{Z}~`+=|<>\n]/gu, '');
};
</script>

<template>
  <div>
    <div
      v-if="isOpen"
      aria-label="Gift Options Radio List"
      data-test="gift-options-list"
      role="radiogroup"
    >
      <RadioGroupItem
        v-model="giftOption"
        position="first"
        :name="`gift-options-${uniqueId}`"
        value="noGift"
      >
        <BaseBodyText>No gift message</BaseBodyText>
      </RadioGroupItem>
      <RadioGroupItem
        v-model="giftOption"
        position="last"
        :name="`gift-options-${uniqueId}`"
        value="includeGiftMessage"
      >
        <BaseBodyText>Add a gift message to the packing slip</BaseBodyText>
        <template v-slot:right>
          <BaseBodyText class="font-semibold">FREE</BaseBodyText>
        </template>

        <template v-slot:bottom>
          <div v-if="giftOption === 'includeGiftMessage'" class="mt-2">
            <TextArea
              v-model="message"
              :name="`sender-message-${uniqueId}`"
              aria-label="gift message"
              data-test="gift-message-textarea"
              :maxLength
              placeholder="Enter your gift message here"
              @input="cleanseEmojis"
              @paste="cleanseEmojis"
            />
            <Input
              v-model="senderName"
              class="mt-1"
              type="text"
              :name="`sender-name-${uniqueId}`"
              :id="`sender-name-${uniqueId}`"
              placeholder="From"
              aria-label="From"
              showPlaceholder
              data-test="sender-name"
            />
          </div>
        </template>
      </RadioGroupItem>
    </div>
    <TextButton v-else @click="isOpen = true">
      <BaseBodyText>Add a free gift message</BaseBodyText>
    </TextButton>
  </div>
</template>
